import { useCallback, useEffect, useRef, useState } from 'react'
import { WbsItemDetail } from '../../../../lib/functions/wbsItem'
import {
  loadWbsItemDetail,
  peekCachedWbsItemDetail,
} from '../wbsItemDetailCache'

export type WbsItemDetailLookup =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'resolved'; detail: WbsItemDetail }
  | { status: 'unavailable' }

const toLookup = (
  detail: WbsItemDetail | null | undefined
): WbsItemDetailLookup => {
  if (detail === undefined) {
    return { status: 'loading' }
  }
  return detail ? { status: 'resolved', detail } : { status: 'unavailable' }
}

/**
 * ホバーカードの詳細情報（getDetail）を遅延取得するフック。
 *
 * マウント時には何もしない。呼び出し側が requestLoad を呼んで初めて取得が始まる
 * （＝実際にホバーされたとき）。本文に並ぶチップの数だけ getDetail が飛ぶ事態を避けるため。
 * キャッシュ済み（wbsItemDetailCache 側）なら requestLoad は即座に resolved/unavailable を返す。
 */
export const useWbsItemDetailOnHover = (
  code: string,
  uuid: string
): { lookup: WbsItemDetailLookup; requestLoad: () => void } => {
  const [lookup, setLookup] = useState<WbsItemDetailLookup>(() => {
    const cached = peekCachedWbsItemDetail(code)
    return cached === undefined ? { status: 'idle' } : toLookup(cached)
  })

  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
    },
    []
  )

  const requestLoad = useCallback(() => {
    const cached = peekCachedWbsItemDetail(code)
    if (cached !== undefined) {
      setLookup(toLookup(cached))
      return
    }
    setLookup(prev => (prev.status === 'idle' ? { status: 'loading' } : prev))
    loadWbsItemDetail(code, uuid).then(detail => {
      if (mountedRef.current) {
        setLookup(toLookup(detail))
      }
    })
  }, [code, uuid])

  return { lookup, requestLoad }
}
